import React, { useState, useEffect } from 'react';
import { coachesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { PhotoUpload, PhotoAvatar } from '../components/PhotoUpload';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Search, Loader2, GraduationCap, Mail, Phone, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Coaches = () => {
  const { isAdmin } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialization: '',
    bio: '',
    status: 'active',
    photo_url: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [tempCoachId, setTempCoachId] = useState(null);

  useEffect(() => {
    loadCoaches();
  }, []);

  const loadCoaches = async () => {
    try {
      const response = await coachesAPI.getAll({});
      setCoaches(response.data);
    } catch (error) {
      toast.error('Failed to load coaches');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (selectedCoach) {
        await coachesAPI.update(selectedCoach.id, formData);
        toast.success('Coach updated successfully');
      } else {
        await coachesAPI.create(formData);
        toast.success('Coach created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadCoaches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save coach');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCoach) return;
    setSubmitting(true);
    try {
      await coachesAPI.delete(selectedCoach.id);
      toast.success('Coach deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedCoach(null);
      loadCoaches();
    } catch (error) {
      toast.error('Failed to delete coach');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      specialization: '',
      bio: '',
      status: 'active',
      photo_url: '',
    });
    setSelectedCoach(null);
    setTempCoachId(`temp-${Date.now()}`); // Generate new temp ID for photo upload
  };

  const openEditDialog = (coach) => {
    setSelectedCoach(coach);
    setFormData({
      name: coach.name,
      email: coach.email,
      phone: coach.phone || '',
      specialization: coach.specialization || '',
      bio: coach.bio || '',
      status: coach.status,
      photo_url: coach.photo_url || '',
    });
    setTempCoachId(coach.id); // Use existing ID for photo upload
    setDialogOpen(true);
  };

  const handlePhotoUpload = (photoUrl) => {
    setFormData(prev => ({ ...prev, photo_url: photoUrl }));
  };

  const filteredCoaches = coaches.filter((coach) =>
    coach.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coach.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (coach.specialization && coach.specialization.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Coaches</h1>
          <p className="text-slate-500">Manage your coaching staff</p>
        </div>
        {isAdmin() && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-coach-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Coach
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search coaches..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-coaches"
        />
      </div>

      {/* Coaches Grid */}
      {filteredCoaches.length === 0 ? (
        <div className="text-center py-12">
          <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-400">No coaches found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCoaches.map((coach) => (
            <Card key={coach.id} className="card-hover" data-testid={`coach-card-${coach.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <PhotoAvatar 
                    photoUrl={coach.photo_url} 
                    name={coach.name} 
                    size="lg" 
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{coach.name}</h3>
                        <Badge variant={coach.status === 'active' ? 'default' : 'secondary'} className="mt-1">
                          {coach.status}
                        </Badge>
                      </div>
                      {isAdmin() && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(coach)}
                            data-testid={`edit-coach-${coach.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedCoach(coach); setDeleteDialogOpen(true); }}
                            className="text-destructive"
                            data-testid={`delete-coach-${coach.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {coach.specialization && (
                      <p className="text-sm text-primary font-medium mt-2">{coach.specialization}</p>
                    )}
                    
                    <div className="mt-3 space-y-1">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{coach.email}</span>
                      </div>
                      {coach.phone && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Phone className="w-4 h-4" />
                          <span>{coach.phone}</span>
                        </div>
                      )}
                    </div>
                    
                    {coach.bio && (
                      <p className="text-sm text-slate-500 mt-3 line-clamp-2">{coach.bio}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCoach ? 'Edit Coach' : 'Add New Coach'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Photo Upload Section */}
            <div className="flex flex-col items-center gap-3 pb-4 border-b">
              <Label className="text-center">Coach Photo</Label>
              <PhotoUpload
                currentPhotoUrl={formData.photo_url}
                entityType="coach"
                entityId={tempCoachId || selectedCoach?.id || `temp-${Date.now()}`}
                onUploadSuccess={handlePhotoUpload}
                size="lg"
              />
              <p className="text-xs text-slate-500">Click to upload a photo</p>
            </div>

            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter coach name"
                data-testid="coach-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                data-testid="coach-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Specialization</Label>
              <Input
                value={formData.specialization}
                onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                placeholder="e.g., Competitive Swimming"
              />
            </div>
            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Brief description..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-coach-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedCoach ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Coach</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedCoach?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Coaches;
